import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useState } from "react";

const BulkNameUpload = ({
  projectId,
  countries: initialCountries = [],
  niceClasses: initialNiceClasses = [],
  onComplete,
  onClose,
}) => {
  const [inputNames, setInputNames] = useState("");
  const [countries] = useState(initialCountries);
  const [niceClasses] = useState(initialNiceClasses.map(c => String(c)));
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const host = process.env.REACT_APP_SERVER;

  const parseNames = () => {
    return inputNames
      .split("\n")
      .map(name => name.trim())
      .filter(name => name !== "");
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const names = parseNames();

    if (names.length === 0) {
      setError("Please enter at least one name");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    const timePerName = 3000;
    const minTimeout = 10000;
    const timeout = Math.min(names.length * timePerName + minTimeout, 300000);

    try {
      const response = await axios.post(
        `${host}/api/projects/${projectId}/add_names_bulk`,
        {
          names,
          countries,
          nice_classes: niceClasses.map(c => parseInt(c, 10)),
        },
        { timeout }
      );

      setResults({
        status: response.data.status,
        message: response.data.message,
        namesSubmitted: names.length,
      });

      if (onComplete && typeof onComplete === "function") {
        onComplete({
          status: "success",
          namesSubmitted: names.length,
        });
      }

      // Clear input after success
      setInputNames("");
    } catch (err) {
      console.error("Error uploading names:", err);
      setError(err.response?.data?.message || "An error occurred while uploading names");
    } finally {
      setIsLoading(false);
    }
  };

  const nameCount = parseNames().length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-base-content/70">Upload Names</span>
        {onClose && (
          <button
            onClick={onClose}
            className="text-base-content/30 hover:text-base-content/60 transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit}>
        <textarea
          value={inputNames}
          onChange={e => setInputNames(e.target.value)}
          placeholder="Enter names, one per line..."
          className="w-full h-32 p-3 text-sm bg-transparent border border-base-content/10 rounded-lg resize-none focus:outline-none focus:border-primary/50 placeholder:text-base-content/30"
          required
        />

        {/* Footer */}
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-base-content/40">
            {nameCount} {nameCount === 1 ? "name" : "names"}
          </span>
          <button
            type="submit"
            disabled={isLoading || nameCount === 0}
            className="px-4 py-2 text-xs font-medium uppercase tracking-wider bg-primary text-primary-content rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="mt-3 p-3 text-sm text-error bg-error/10 rounded-lg">
          {error}
        </div>
      )}

      {/* Success */}
      {results && (
        <div className="mt-3 p-3 text-sm text-success bg-success/10 rounded-lg">
          {results.message || `${results.namesSubmitted} names submitted for processing`}
        </div>
      )}
    </div>
  );
};

export default BulkNameUpload;
